import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { App, type URLOpenListenerEvent } from '@capacitor/app';

import { isNativePlatform } from 'services/auth/platform';
import { connectInstance } from 'services/config/connectInstance';

/**
 * Handles `atlasone://connect?host=<webHost>` deep links that open the app from a customer instance's website. Native only.
 * Reads the cold-start launch URL once and listens for warm-start URL opens, dedupes, connects to the instance, then routes to login.
 * Mounted inside the router so it can navigate.
 */
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const lastHandledUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!isNativePlatform()) return undefined;

    const handleUrl = async (url: string | null | undefined) => {
      if (!url || url === lastHandledUrl.current) return;
      lastHandledUrl.current = url;

      let parsed: URL;
      try {
        parsed = new URL(url);
      } catch {
        return;
      }

      // atlasone://connect?host=dev.atlasone.co
      if (parsed.protocol !== 'atlasone:' || parsed.host !== 'connect') return;

      const host = parsed.searchParams.get('host');
      if (!host) return;

      try {
        await connectInstance(host);
        navigate('/login');
      } catch {
        // connectInstance surfaces user-facing errors on the InstanceConnect screen; the gate shows it
        // when unconfigured. Swallow here so a bad deep link does not crash the listener.
      }
    };

    // Cold start: the link launched the app, so the URL is not delivered via the event.
    App.getLaunchUrl().then((result) => handleUrl(result?.url));

    // Warm start: the app was already running.
    const listenerHandle = App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      handleUrl(event.url);
    });

    return () => {
      listenerHandle.then((handle) => handle.remove());
    };
  }, [navigate]);

  return null;
};

export default DeepLinkHandler;
